import { useState, useRef } from "react";
import { Button } from "../../ui/button";
import { X } from "lucide-react";
import React from "react";

interface FileInfo {
  name: string;
  size: number;
  file: File;
}

interface FileDropZoneProps {
  selectedFile: FileInfo | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
}

export function FileDropZone({ selectedFile, onFileSelect, onFileRemove }: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndSelectFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndSelectFile(files[0]);
    }
  };

  const validateAndSelectFile = (file: File) => {
    // Define accepted MIME types
    const acceptedTypes = [
      'audio/mpeg',        // .mp3
      'audio/wav',         // .wav
      'audio/webm',        // .webm
      'video/mp4',         // .mp4
      'video/quicktime',   // .mov
      'video/webm',        // .webm
      'video/x-m4v',       // .m4v
      'audio/mp4',         // .m4a
      'audio/aac',         // .aac
      'audio/x-m4a'        // Alternative MIME type for .m4a on some systems
    ];

    // Check if file type is accepted
    if (!acceptedTypes.includes(file.type)) {
      console.error('Invalid file type:', file.type);
      alert('Please select an audio or video file (MP3, WAV, MP4, MOV, WebM, M4A, AAC)');
      return;
    }

    // Check file size (150MB limit)
    const maxSize = 150 * 1024 * 1024; // 150MB in bytes
    if (file.size > maxSize) {
      alert('File size must be less than 150MB');
      return;
    }

    onFileSelect(file);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-300 ${
        isDragging 
          ? "border-primary bg-primary/5" 
          : "border-border bg-background"
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!selectedFile ? (
        <div className="space-y-4">
          <p className="text-lg text-foreground">Drop your audio or video files here</p>
          <div>
            <input
              type="file"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".mp3,.wav,.m4a,.aac,.mp4,.mov,.webm,.m4v,audio/*,video/*"
            />
            <Button onClick={() => fileInputRef.current?.click()} className="button button-default">
              Choose File
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Supported formats: MP3, WAV, MP4, MOV, WebM, M4A, AAC (max 150MB)
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-foreground" title={selectedFile.name}>
                {selectedFile.name}
              </p>
              <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onFileRemove}
              className="text-muted-foreground hover:text-destructive shrink-0 ml-2"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
